import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faArrowLeft,
  faClipboardList,
  faClipboardCheck,
  faLayerGroup,
  faBullhorn,
  faComments,
  faVideo,
  faBriefcase,
  faDownload,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import {
  authFetch,
  formatDate,
  formatDateTime,
  MODULE_TYPE_BADGE,
  MODULE_TYPE_LABELS,
  MODERATION_BADGE,
  MODERATION_LABELS,
} from '../../utils/http';
import AnnouncementsPanel from '../../components/course/AnnouncementsPanel';
import DiscussionsPanel from '../../components/course/DiscussionsPanel';
import WebinarsPanel from '../../components/course/WebinarsPanel';
import WorkplaceStaffPanel from '../../components/course/WorkplaceStaffPanel';

interface Module {
  id: string;
  title: string;
  moduleType: number;
  orderIndex: number;
}

interface Assessment {
  assignmentId: string;
  title: string;
  instructions: string;
  criteria: string | null;
  assessmentType: number;
  moduleTitle: string | null;
  maxMark: number;
  passingMark: number;
  dueDate: string;
  isPublished: boolean;
  attachmentUrl: string | null;
  attachmentFileName: string | null;
  submittedCount: number;
  gradedCount: number;
  moderatedCount: number;
}

interface Submission {
  submissionId: string;
  assignmentId: string;
  assignmentTitle: string;
  assessmentType: number;
  maxMark: number;
  studentName: string;
  studentIdNumber: string;
  submittedAt: string | null;
  fileUrl: string | null;
  fileName: string | null;
  submissionNote: string | null;
  grade: number | null;
  feedback: string | null;
  gradedAt: string | null;
  moderationOutcome: number | null;
  moderationComment: string | null;
  moderatedAt: string | null;
}

interface CourseDetail {
  learnershipId: string;
  title: string;
  saqaId: string;
  modules: Module[];
  assessments: Assessment[];
  submissions: Submission[];
}

type Tab = 'assessments' | 'moderation' | 'modules' | 'announcements' | 'discussions' | 'sessions' | 'workplace';

/** Read-only course view for a moderator, with the ability to record moderation outcomes. */
export default function ModeratorCoursePage() {
  const { learnershipId } = useParams<{ learnershipId: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<Tab>('assessments');

  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [pendingOnly, setPendingOnly] = useState(false);

  const [target, setTarget] = useState<Submission | null>(null);
  const [outcome, setOutcome] = useState('0');
  const [comment, setComment] = useState('');
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    if (!learnershipId) return;
    setIsLoading(true);
    const res = await authFetch<CourseDetail>(accessToken, `/api/moderator/courses/${learnershipId}`);
    if (res.ok && res.data) {
      setCourse(res.data);
      setError('');
    } else {
      setError(res.message || 'Failed to load course.');
    }
    setIsLoading(false);
  }, [accessToken, learnershipId]);

  useEffect(() => {
    load();
  }, [load]);

  const openModerate = (s: Submission) => {
    setTarget(s);
    setOutcome(s.moderationOutcome != null ? String(s.moderationOutcome) : '0');
    setComment('');
    setFormError('');
  };

  const saveModeration = async () => {
    if (!target) return;
    setFormError('');
    if (!comment.trim()) {
      setFormError('A comment is required.');
      return;
    }
    setIsSaving(true);
    const res = await authFetch(accessToken, `/api/moderator/submissions/${target.submissionId}/moderate`, {
      method: 'POST',
      body: { outcome: parseInt(outcome, 10), comment },
    });
    setIsSaving(false);
    if (!res.ok) {
      setFormError(res.message);
      return;
    }
    setTarget(null);
    load();
  };

  const submissions = (course?.submissions ?? []).filter((s) => {
    if (s.grade == null) return false; // moderation applies to graded work
    if (typeFilter !== 'all' && String(s.assessmentType) !== typeFilter) return false;
    if (pendingOnly && s.moderationOutcome != null) return false;
    return true;
  });

  const tabs: { id: Tab; label: string; icon: any }[] = [
    { id: 'assessments', label: 'Assessments', icon: faClipboardList },
    { id: 'moderation', label: 'Moderation', icon: faClipboardCheck },
    { id: 'modules', label: 'Modules', icon: faLayerGroup },
    { id: 'announcements', label: 'Announcements', icon: faBullhorn },
    { id: 'discussions', label: 'Discussions', icon: faComments },
    { id: 'sessions', label: 'Live Sessions', icon: faVideo },
    { id: 'workplace', label: 'Workplace Log', icon: faBriefcase },
  ];

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <button
          onClick={() => navigate('/moderator/courses')}
          className="flex items-center gap-2 text-sm font-semibold text-brand-700 hover:text-brand-800 mb-2"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
          Back to courses
        </button>
        <h1 className="text-2xl font-bold text-brand-950">{course?.title ?? 'Course'}</h1>
        {course && <p className="text-xs text-brand-900/50 mt-0.5">SAQA {course.saqaId} · read-only access</p>}
      </div>

      <div className="p-8">
        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoading && error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

        {!isLoading && course && (
          <>
            <div className="flex gap-1 border-b border-canvas-300 bg-white rounded-t-2xl mb-6 overflow-x-auto">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold border-b-2 whitespace-nowrap transition-all ${
                    tab === t.id ? 'border-brand-600 text-brand-700' : 'border-transparent text-brand-900/60 hover:text-brand-900'
                  }`}
                >
                  <FontAwesomeIcon icon={t.icon} className="h-4 w-4" />
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 'assessments' && (
              <div className="space-y-3">
                {course.assessments.length === 0 && (
                  <div className="card-surface p-12 text-center">
                    <FontAwesomeIcon icon={faClipboardList} className="h-12 w-12 text-brand-300 mb-4" />
                    <p className="text-brand-900/60">No assessments have been created for this course.</p>
                  </div>
                )}
                {course.assessments.map((a) => (
                  <div key={a.assignmentId} className="card-surface p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h4 className="font-bold text-brand-950 flex items-center gap-2 flex-wrap">
                          {a.title}
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${MODULE_TYPE_BADGE[a.assessmentType]}`}>
                            {MODULE_TYPE_LABELS[a.assessmentType]}
                          </span>
                          {!a.isPublished && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-gray-100 text-gray-700">Draft</span>}
                        </h4>
                        <p className="text-xs text-brand-900/50 mt-0.5">
                          {a.moduleTitle ? `${a.moduleTitle} · ` : ''}Due {formatDate(a.dueDate)} · {a.maxMark} marks (pass {a.passingMark})
                        </p>
                      </div>
                      <div className="text-xs text-brand-900/60 text-right flex-shrink-0">
                        <p>{a.submittedCount} submitted</p>
                        <p>{a.gradedCount} graded</p>
                        <p>{a.moderatedCount} moderated</p>
                      </div>
                    </div>
                    {a.instructions && <p className="text-sm text-brand-900/80 mt-3 whitespace-pre-wrap">{a.instructions}</p>}
                    {a.criteria && (
                      <p className="text-xs text-brand-900/70 mt-3 bg-canvas-50 border border-canvas-200 rounded-lg p-3 whitespace-pre-wrap">
                        <span className="font-semibold">Assessment criteria:</span> {a.criteria}
                      </p>
                    )}
                    {a.attachmentUrl && (
                      <a
                        href={a.attachmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-3 text-xs font-semibold text-brand-700 hover:text-brand-800"
                      >
                        <FontAwesomeIcon icon={faDownload} className="h-3 w-3" />
                        {a.attachmentFileName ?? 'Download attachment'}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}

            {tab === 'moderation' && (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="px-4 py-2 border border-canvas-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="all">All component types</option>
                    <option value="0">Knowledge</option>
                    <option value="1">Practical</option>
                    <option value="2">Workplace</option>
                  </select>
                  <label className="flex items-center gap-2 text-sm text-brand-900/70">
                    <input type="checkbox" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} />
                    Not yet moderated
                  </label>
                </div>

                {submissions.length === 0 ? (
                  <div className="card-surface p-12 text-center">
                    <FontAwesomeIcon icon={faClipboardCheck} className="h-12 w-12 text-brand-300 mb-4" />
                    <p className="text-brand-900/60">No graded submissions to moderate.</p>
                  </div>
                ) : (
                  <div className="card-surface overflow-hidden divide-y divide-canvas-200">
                    {submissions.map((s) => (
                      <div key={s.submissionId} className="p-5">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-brand-950">
                              {s.studentName} <span className="text-xs font-normal text-brand-900/50">({s.studentIdNumber})</span>
                            </p>
                            <p className="text-xs text-brand-900/50 flex items-center gap-2 mt-0.5">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${MODULE_TYPE_BADGE[s.assessmentType]}`}>
                                {MODULE_TYPE_LABELS[s.assessmentType]}
                              </span>
                              {s.assignmentTitle} · submitted {formatDateTime(s.submittedAt)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-brand-950">
                              {s.grade}/{s.maxMark}
                            </p>
                            {s.moderationOutcome != null && (
                              <span className={`inline-block mt-1 px-2.5 py-1 rounded-full text-xs font-semibold ${MODERATION_BADGE[s.moderationOutcome]}`}>
                                {MODERATION_LABELS[s.moderationOutcome]}
                              </span>
                            )}
                          </div>
                        </div>

                        {s.submissionNote && <p className="text-xs text-brand-900/70 mt-2">Learner note: {s.submissionNote}</p>}
                        {s.feedback && (
                          <p className="text-xs text-brand-900/70 mt-2 bg-canvas-50 border border-canvas-200 rounded-lg p-3">
                            <span className="font-semibold">Facilitator feedback:</span> {s.feedback}
                          </p>
                        )}
                        {s.moderationComment && (
                          <p className="text-xs text-brand-900/70 mt-2 bg-brand-50 border border-brand-100 rounded-lg p-3">
                            <span className="font-semibold">Moderator comment ({formatDate(s.moderatedAt)}):</span> {s.moderationComment}
                          </p>
                        )}

                        <div className="flex gap-2 mt-3">
                          {s.fileUrl && (
                            <a
                              href={s.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1.5 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors flex items-center gap-1.5"
                            >
                              <FontAwesomeIcon icon={faDownload} className="h-3 w-3" />
                              {s.fileName ?? 'Evidence'}
                            </a>
                          )}
                          <button
                            onClick={() => openModerate(s)}
                            className="px-3 py-1.5 bg-brand-gradient text-white text-xs font-semibold rounded-lg hover:shadow-lg transition-all"
                          >
                            {s.moderationOutcome != null ? 'Update moderation' : 'Moderate'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === 'modules' && (
              <div className="card-surface divide-y divide-canvas-200 overflow-hidden">
                {course.modules.map((m) => (
                  <div key={m.id} className="px-5 py-4 flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-brand-950">
                      {m.orderIndex}. {m.title}
                    </p>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${MODULE_TYPE_BADGE[m.moduleType]}`}>{MODULE_TYPE_LABELS[m.moduleType]}</span>
                  </div>
                ))}
                {course.modules.length === 0 && <p className="p-8 text-center text-brand-900/60">No modules defined.</p>}
              </div>
            )}

            {learnershipId && tab === 'announcements' && <AnnouncementsPanel learnershipId={learnershipId} canManage={false} />}
            {learnershipId && tab === 'discussions' && <DiscussionsPanel learnershipId={learnershipId} readOnly />}
            {learnershipId && tab === 'sessions' && <WebinarsPanel learnershipId={learnershipId} canManage={false} />}
            {learnershipId && tab === 'workplace' && <WorkplaceStaffPanel learnershipId={learnershipId} />}
          </>
        )}
      </div>

      {target && (
        <div className="fixed inset-0 bg-brand-950/40 z-50 flex items-center justify-center px-4 py-8 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 my-auto">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-brand-950">Moderate submission</h3>
              <button onClick={() => setTarget(null)} className="p-1.5 hover:bg-canvas-100 rounded-lg text-brand-900/60">
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-brand-900/60 mb-5">
              {target.studentName} · {target.assignmentTitle} · {target.grade}/{target.maxMark}
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">Outcome</label>
                <select
                  value={outcome}
                  onChange={(e) => setOutcome(e.target.value)}
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="0">Agree with facilitator's assessment</option>
                  <option value="1">Disagree with facilitator's assessment</option>
                  <option value="2">Refer back to facilitator</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">
                  Comment <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              {formError && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{formError}</p>}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setTarget(null)} className="px-5 py-2 border border-canvas-300 text-brand-950 font-semibold rounded-lg hover:bg-canvas-100 transition-colors text-sm">
                Cancel
              </button>
              <button
                onClick={saveModeration}
                disabled={isSaving}
                className="px-5 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
              >
                {isSaving && <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" />}
                Save moderation
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
